import type { RenderFrameView } from './RenderBackend.js'

export const MAX_MOTION_BLUR_SCREEN_DISTANCE = 64
export const MAX_MOTION_BLUR_TAPS = 3
export const MIN_MOTION_BLUR_SCREEN_DISTANCE = 0.75

export interface MutableMotionVector {
  x: number
  y: number
}

/**
 * Motion blur is geometry sampled from the previous prepared frame, never a
 * retained framebuffer. That keeps layout regeneration and seeking free of
 * persistent afterimages while producing the same trail on both backends.
 */
export function motionBlurTapCount(frame: RenderFrameView): number {
  if (
    frame.project.animation.motionBlur <= 0
    || frame.deltaSeconds <= 0
    || frame.particles.previousPositionX === undefined
    || frame.particles.previousPositionY === undefined
  ) return 0
  if (frame.quality.tier === 'minimum') return 0
  if (frame.quality.tier === 'low') return 1
  if (frame.quality.tier === 'balanced') return 2
  return MAX_MOTION_BLUR_TAPS
}

/** Tap 1 is nearest the crisp particle; the highest tap is farthest behind. */
export function motionBlurTapDistance(tap: number, tapCount: number, strength: number): number {
  if (tapCount <= 0 || tap <= 0 || strength <= 0) return 0
  return Math.max(0, Math.min(1, strength)) * Math.min(1, tap / tapCount)
}

/** Farthest samples are faintest so the current position remains visually dominant. */
export function motionBlurTapAlpha(tap: number, tapCount: number, strength: number): number {
  if (tapCount <= 0 || tap <= 0 || strength <= 0) return 0
  const normalized = Math.min(1, tap / tapCount)
  return Math.max(0, Math.min(1, strength)) * (0.2 - normalized * 0.12)
}

/**
 * Writes a backwards velocity vector clamped in screen space. Returns false
 * for stationary/subpixel/non-finite motion so ghost taps cannot darken a
 * paused particle or amplify malformed input.
 */
export function writeMotionBlurVector(
  output: MutableMotionVector,
  currentX: number,
  currentY: number,
  previousX: number | undefined,
  previousY: number | undefined,
  viewportZoom: number,
): boolean {
  output.x = 0
  output.y = 0
  if (
    previousX === undefined
    || previousY === undefined
    || !Number.isFinite(currentX)
    || !Number.isFinite(currentY)
    || !Number.isFinite(previousX)
    || !Number.isFinite(previousY)
    || !Number.isFinite(viewportZoom)
  ) return false
  const deltaX = previousX - currentX
  const deltaY = previousY - currentY
  const screenDistance = Math.hypot(deltaX, deltaY) * Math.abs(viewportZoom)
  if (!Number.isFinite(screenDistance) || screenDistance < MIN_MOTION_BLUR_SCREEN_DISTANCE) return false
  const scale = Math.min(1, MAX_MOTION_BLUR_SCREEN_DISTANCE / screenDistance)
  output.x = deltaX * scale
  output.y = deltaY * scale
  return true
}
